// eslint-disable-next-line simple-import-sort/imports
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import TabContext from '@mui/lab/TabContext';
import {
  Box,
  CircularProgress,
  Tab,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import SaveToAnotherFolderModal from '~/components/folders/SaveToAnotherFolderModal';
import CreateEditFolderModal from '~/components/folders/CreateEditFolderModal';
import { ConfirmModal } from '~/components/ui/ConfirmModal/ConfirmModal';
import DownloadContent from '~/components/ui/DownloadContent';
import { Text } from '~/components/ui/text';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import QrTableRowItem from '~/pages/MyQR/components/QrTable/QrTableRowItem';
import { useActivateQrs, useDeactivateQrs, useDeleteQrs, useGetQrs } from '~/queries/qrCodeTable';
import { getLoggedInUser } from '~/store/reducers/account/selector';
import useSidebarStore from '~/store/useSidebarStore';
import { QrCodeStatus } from '~/types/qrCode';

import DeleteImg from '~/assets/icons/modal/delete.svg';
import NoContent from '../../assets/no-content.png';
import BottomBar from '../BottomBar/BottomBar';

import { StyledChecbox, StyledImage, StyledTabList } from './styles';
import * as S from './styles';

const initialParams = {
  type: '',
  page_size: 5,
  page: 1,
};

export default function QRTable({ onDetailClick }) {
  const { t } = useTranslation();
  const routeParams = useParams<{ folderId: string | undefined }>();
  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const { isSubscriptionValid } = useSelector(getLoggedInUser);

  const [params, setParams] = useState(initialParams);
  const [imageModal, setImageModal] = useState(null);
  const [downloadByIdModal, setDownloadByIdModal] = useState(null);
  const [deleteItemsModal, setDeleteItemsModal] = useState(null);
  const [checked, setChecked] = useState<
    { id: number; type: 'DYNAMIC' | 'STATIC'; status: QrCodeStatus; folderId: null | number }[]
  >([]);
  const [isSaveToAnotherFolderModalOpen, setIsSaveToAnotherFolderModalOpen] = useState(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);

  const allCheckedAreDraftOrStatic = checked.every((item) => item.status === 'DRAFT' || item.type === 'STATIC');
  // const allCheckedAreStatic = checked.every((item) => item.type === 'STATIC');

  const { data = {}, isFetching } = useGetQrs({
    ...params,
    ...(routeParams.folderId ? { folder: routeParams.folderId } : {}),
  });

  const { isDesktopSidebarOpen } = useSidebarStore();

  const leftSidebarWidth = isDesktopSidebarOpen ? 323 : 160;

  const { mutate: deleteMyQrItems } = useDeleteQrs();
  const { mutate: activateMyQrItems } = useActivateQrs();
  const { mutate: deactivateMyQrItems } = useDeactivateQrs();
  const { results = [], count = 0 } = data;
  const downloadIdsWithTypes = results
    .filter((item) => {
      const idsToDownload = downloadByIdModal?.id;
      return Array.isArray(idsToDownload) ? idsToDownload?.includes(item.id) : idsToDownload === item.id;
    })
    ?.map((item) => {
      return {
        id: item.id,
        type: item.micrositeType ? item.micrositeType : item.content?.type,
      };
    });

  const handleActivate = () => {
    if (allCheckedAreDraftOrStatic) {
      return null;
    }
    return () => {
      const filteredAndMappedChecked = checked
        .filter((item) => item.status !== 'DRAFT' && item.type !== 'STATIC')
        .filter((item) => item.type !== 'STATIC')
        .map((i) => i.id);
      activateMyQrItems(filteredAndMappedChecked);
    };
  };

  const handleDeactivate = () => {
    if (allCheckedAreDraftOrStatic) {
      return null;
    }
    return () => {
      const filteredAndMappedChecked = checked
        .filter((item) => item.status !== 'DRAFT' && item.type !== 'STATIC')
        .filter((item) => item.type !== 'STATIC')
        .map((i) => i.id);
      deactivateMyQrItems(filteredAndMappedChecked);
    };
  };

  const handleTabItemClick = (_, newValue) => {
    setParams((prev) => ({ ...prev, type: newValue }));
    setChecked([]);
  };

  const handleAllChecked = (e) => {
    if (e.target.checked) {
      setChecked(
        results.map((item) => {
          return { id: item.id, type: item.type, status: item.status, folderId: item.folder ? item.folder.id : null };
        }),
      );
    } else {
      setChecked([]);
    }
  };

  return (
    <>
      <Box position="relative" className={isSubscriptionValid ? '' : 'is-not-valid'} sx={S.subscriptionEnable}>
        {isFetching && (
          <Box sx={S.TableLoader}>
            <CircularProgress />
          </Box>
        )}
        <TabContext value={params.type}>
          <StyledTabList onChange={handleTabItemClick}>
            <Tab label={<Text variant="h3">{t('labels:all')}</Text>} value="" />
            <Tab label={<Text variant="h3">{t('labels:dynamic')}</Text>} value="DYNAMIC" />
            <Tab label={<Text variant="h3">{t('labels:static')}</Text>} value="STATIC" />
          </StyledTabList>
        </TabContext>
        {results.length ? (
          <>
            <TableContainer sx={isDesktop ? { maxWidth: `calc(100dvw - ${leftSidebarWidth + 20}px)` } : undefined}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={S.tableHeadWrapper}>
                      <StyledChecbox
                        indeterminate={false}
                        checked={results?.length === checked?.length}
                        onChange={handleAllChecked}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((row) => {
                    return (
                      <QrTableRowItem
                        key={row.id}
                        row={row}
                        checked={checked}
                        setChecked={setChecked}
                        setDeleteItemsModal={setDeleteItemsModal}
                        onDetailClick={onDetailClick}
                        setDownloadByIdModal={setDownloadByIdModal}
                        setImageModal={setImageModal}
                        currentFetchParams={params}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              sx={S.Pagination}
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={count}
              rowsPerPage={params.page_size || 5}
              page={params.page - 1}
              labelRowsPerPage={t('other:rowsPerPage')}
              onPageChange={(_, page) => {
                setParams((prev) => ({ ...prev, page: page + 1 }));
                setChecked([]);
              }}
              onRowsPerPageChange={(e) => setParams((prev) => ({ ...prev, page_size: e.target.value, page: 1 }))}
            />
          </>
        ) : (
          <Box sx={S.noQrs}>
            <StyledImage src={NoContent} alt="No content" />
            <Text variant="h3" sx={{ color: 'var(--grey400)', maxWidth: '500px', margin: '0 auto' }}>
              {routeParams.folderId ? t('folders:noQRsInFolder') : t('qrTable:notCreated')}
            </Text>
          </Box>
        )}
      </Box>

      {/* Bottom Action Bar */}
      <BottomBar
        number={checked.length}
        onDelete={() => setDeleteItemsModal(checked.map((item) => item.id))}
        onActivate={handleActivate()}
        onDeactivate={handleDeactivate()}
        onDownload={() => setDownloadByIdModal({ id: checked.map((item) => item.id), name: '' })}
        onCancel={() => setChecked([])}
        onSaveTo={() => setIsSaveToAnotherFolderModalOpen(true)}
      />

      {/* Image modal  */}
      <ConfirmModal onClose={() => setImageModal(null)} open={Boolean(imageModal)} img={imageModal} />

      {/* Download Modal */}
      <ConfirmModal onClose={() => setDownloadByIdModal(null)} open={Boolean(downloadByIdModal)}>
        <DownloadContent downloadIdsWithTypes={downloadIdsWithTypes} imageName={downloadByIdModal?.name} />
      </ConfirmModal>

      {/* Delete modal  */}
      <ConfirmModal
        onClose={() => setDeleteItemsModal(null)}
        open={Boolean(deleteItemsModal)}
        img={DeleteImg}
        confirmText={t('buttons:delete')}
        isDelateStyle
        onConfirm={() => {
          deleteMyQrItems(deleteItemsModal, {
            onSuccess: () => {
              setParams((prev) => ({ ...prev, page: 1 }));
              setDeleteItemsModal(null);
              setChecked([]);
            },
            onError: () => {
              setDeleteItemsModal(null);
              setChecked([]);
            },
          });
        }}
      >
        <Text variant="h2">{t('popup:qrDeleteSubtitle')}</Text>
      </ConfirmModal>
      {isSaveToAnotherFolderModalOpen && (
        <SaveToAnotherFolderModal
          isOpen={isSaveToAnotherFolderModalOpen}
          onClose={() => setIsSaveToAnotherFolderModalOpen(false)}
          openCreateModal={() => setIsCreateFolderModalOpen(true)}
          selectedQRs={checked}
          setSelectedQRs={setChecked}
        />
      )}
      {isCreateFolderModalOpen && (
        <CreateEditFolderModal
          isOpen={isCreateFolderModalOpen}
          onClose={() => setIsCreateFolderModalOpen(false)}
          type="create"
        />
      )}
    </>
  );
}
